<template>
  <div class="qc-report-print">
    <div class="print-header">
      <el-button type="primary" size="small" @click="handlePrint">打印</el-button>
    </div>

    <div class="print-content">
      <div class="print-content-inner" id="printArea">
        <!-- 标题 -->
        <h2 class="report-title">
          {{ weeklySummary ? "车辆运营安全与服务监控巡查周报" : "车辆运营安全与服务监控巡查日志" }}
        </h2>

        <!-- 基本信息 -->
        <div class="info-section">
          <div class="info-row">
            <span class="info-label">巡查日期：</span>
            <span class="info-value">{{ fillTime }}</span>
            <span class="info-label">巡查人员：</span>
            <span class="info-value">郑梦文</span>
          </div>
        </div>

        <!-- 巡查类目列表 -->
        <div class="category-section" v-for="(category, index) in categoryList" :key="index">
          <div class="category-header">
            <span class="category-title">{{ category.title }}</span>
          </div>
          <table class="category-table">
            <tbody>
              <tr>
                <td class="table-label">类目描述</td>
                <td class="table-value" colspan="3">{{ category.checkItems }}</td>
              </tr>
              <tr>
                <td class="table-label">巡查车辆/司机数量</td>
                <td class="table-value">{{ category.driverCount }}</td>
                <td class="table-label">巡查率</td>
                <td class="table-value">{{ category.checkRate }}</td>
              </tr>
              <tr>
                <td class="table-label">不合格司机数量</td>
                <td class="table-value">{{ category.needImproveCount }}</td>
                <td class="table-label">不合格司机率</td>
                <td class="table-value">{{ category.needImproveRate }}</td>
              </tr>
              <tr>
                <td class="table-label">巡查名单</td>
                <td class="table-value" colspan="3">
                  <div class="driver-nos">
                    <span
                      v-for="(driverNo, idx) in category.driverNoList"
                      :key="idx"
                      class="driver-no"
                    >
                      {{ driverNo }}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 不合格司机汇总 -->
        <div class="summary-section">
          <div class="summary-title">不合格司机汇总</div>

          <div
            v-for="(category, index) in categoryList"
            :key="'summary-' + index"
            class="summary-table-wrapper"
          >
            <div class="summary-category-title">{{ category.summaryTitle }}</div>
            <table
              class="summary-table"
              v-if="category.detailList && category.detailList.length > 0"
            >
              <thead>
                <tr>
                  <th width="15%">司机编号</th>
                  <th width="40%">不合格事项</th>
                  <th width="15%">累计次数</th>
                  <th width="30%">整改举措</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(detail, idx) in category.detailList" :key="idx">
                  <td>{{ detail.driverNo }}</td>
                  <td>{{ detail.checkItem }}</td>
                  <td>{{ detail.totalTimes }}</td>
                  <td>{{ detail.result }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="empty-table">无不合格司机</div>
          </div>
        </div>

        <!-- 备注 -->
        <div class="remark-section">
          <div class="remark-label">巡查备注：</div>
          <div class="remark-content">{{ remarks || "无" }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getDailyQcSummary } from "@/api";

export default {
  name: "QcReportPrint",
  data() {
    return {
      date: "",
      fillTime: "",
      categoryList: [],
      remarks: "",
      weeklySummary: false,
      startDate: "",
      endDate: ""
    };
  },
  created() {
    const { date, weeklySummary } = this.$route.query;
    if (date) {
      this.date = date;
    }
    if (weeklySummary) {
      this.weeklySummary = weeklySummary === "true";
    }
    this.loadData();
  },
  methods: {
    loadData() {
      const param = {
        date: this.date,
        weeklySummary: this.weeklySummary
      };
      // 加载数据
      getDailyQcSummary(param)
        .then((res) => {
          const { list, startDate, endDate } = res.data || {};
          if (this.weeklySummary) {
            this.fillTime = `${startDate} - ${endDate}`;
          } else {
            this.fillTime = `${this.date} 18:00`;
          }
          this.processData(list, startDate, endDate);
        })
        .catch((err) => {
          console.error(err);
          this.$message.error("加载数据失败");
        });
    },
    processData(dataList) {
      // 收集所有备注
      const allRemarks = [];

      // 处理每个类目的数据
      this.categoryList = dataList.map((item) => {
        // 确定类目类型（通过检查 checkItems 来判断）
        const categoryType = item.type;

        // 处理司机编号列表
        const driverNos = item.driverNos || "";
        const driverNoList = driverNos ? driverNos.split(",").filter((no) => no.trim()) : [];

        // 收集备注
        if (item.remarks) {
          allRemarks.push(item.remarks);
        }

        return {
          ...item,
          driverNoList,
          title: categoryType + "巡查类目",
          summaryTitle: categoryType + "巡查不合格司机"
        };
      });

      // 按照固定顺序排序：安全硬件、安全行为、服务
      const orderMap = { 安全硬件: 1, 安全行为: 2, 服务: 3, 其他: 4 };
      this.categoryList.sort((a, b) => {
        const aType = this.getCategoryType(a.title);
        const bType = this.getCategoryType(b.title);
        return (orderMap[aType] || 99) - (orderMap[bType] || 99);
      });

      // 合并备注
      this.remarks = allRemarks.join("；") || "";
    },
    getCategoryType(title) {
      if (title.includes("安全硬件")) return "安全硬件";
      if (title.includes("安全行为")) return "安全行为";
      if (title.includes("服务")) return "服务";
      return "其他";
    },
    handlePrint() {
      window.print();
    }
  }
};
</script>

<style lang="less" scoped>
.qc-report-print {
  .print-header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 20px;
    @media print {
      display: none;
    }
  }

  .print-content {
    display: flex;
    justify-content: center;
    padding: 20px;

    .print-content-inner {
      width: 210mm;
      min-height: 297mm;
      padding: 20mm;
      background: white;
      box-sizing: border-box;

      .report-title {
        text-align: center;
        font-size: 24px;
        font-weight: bold;
        margin: 0 0 20px 0;
        padding-bottom: 10px;
        border-bottom: 2px solid #000;
      }

      .report-note {
        margin-bottom: 20px;
        .note-text {
          font-size: 12px;
          color: #666;
        }
      }

      .info-section {
        margin-bottom: 30px;
        display: flex;
        justify-content: flex-end;
        .info-row {
          margin-bottom: 10px;
          font-size: 14px;
          .info-label {
            font-weight: bold;
          }
          .info-value {
            margin-right: 20px;
          }
        }
      }

      .category-section {
        margin-bottom: 30px;
        page-break-inside: avoid;
        page-break-before: auto;

        .category-header {
          margin-bottom: 10px;
          page-break-after: avoid;
          .category-title {
            font-size: 16px;
            font-weight: bold;
          }
        }

        .category-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
          border: 1px solid #000;
          margin-top: 10px;

          tbody {
            tr {
              td {
                padding: 10px 8px;
                border: 1px solid #000;
                vertical-align: top;

                &.table-label {
                  width: 20%;
                  background: #f5f5f5;
                  font-weight: bold;
                  text-align: center;
                }

                &.table-value {
                  width: 30%;
                  text-align: left;
                  padding-left: 12px;
                }
              }

              &:nth-child(even) {
                background: #fafafa;
              }
            }
          }

          .driver-nos {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
            line-height: 1.8;
            .driver-no {
              display: inline-block;
              font-size: 13px;
              margin-right: 8px;
            }
          }
        }
      }

      .summary-section {
        margin-top: 40px;
        .summary-title {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 20px;
          text-align: center;
          page-break-after: avoid;
        }

        .summary-table-wrapper {
          margin-bottom: 30px;
          page-break-inside: avoid;
          page-break-before: auto;

          .summary-category-title {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 10px;
            padding-left: 10px;
            border-left: 4px solid #000;
            page-break-after: avoid;
          }

          .summary-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 13px;
            margin-bottom: 20px;
            border: 1px solid #000;

            thead {
              background: #f5f5f5;
              tr {
                th {
                  padding: 10px 8px;
                  border: 1px solid #000;
                  text-align: center;
                  font-weight: bold;
                  background: #e8e8e8;
                }
              }
            }

            tbody {
              tr {
                td {
                  padding: 8px;
                  border: 1px solid #000;
                  text-align: center;
                }
                &:nth-child(even) {
                  background: #fafafa;
                }
              }
            }
          }

          .empty-table {
            text-align: center;
            padding: 20px;
            color: #999;
            font-size: 14px;
          }
        }
      }

      .remark-section {
        margin-top: 40px;
        font-size: 14px;
        .remark-label {
          font-weight: bold;
          margin-bottom: 10px;
        }
        .remark-content {
          min-height: 60px;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          white-space: pre-wrap;
          word-wrap: break-word;
        }
      }
    }
  }
}

@media print {
  * {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  html,
  body {
    margin: 0;
    padding: 0;
    width: 100%;
    height: auto;
  }

  .qc-report-print {
    margin: 0;
    padding: 0;
    width: 100%;

    .print-content {
      padding: 0;
      margin: 0;
      width: 100%;

      .print-content-inner {
        width: 100%;
        max-width: 100%;
        padding: 0 20mm 20mm 20mm;
        box-shadow: none;
        margin: 0;
        box-sizing: border-box;
        background: white;
        /* 上边距由 @page 的 margin-top 提供 */
      }
    }
  }

  /* 设置页面边距，确保每页都有上边距 */
  @page {
    size: A4;
    margin: 20mm;
  }

  /* 防止表格在分页时被截断 */
  table {
    page-break-inside: auto;
  }

  tr {
    page-break-inside: avoid;
    page-break-after: auto;
  }

  thead {
    display: table-header-group;
  }

  tfoot {
    display: table-footer-group;
  }

  /* 确保分页时标题不会被单独留在页面底部 */
  .category-header,
  .summary-category-title,
  .summary-title {
    page-break-after: avoid;
  }

  /* 确保表格标题行在分页时重复显示 */
  .summary-table thead {
    display: table-header-group;
  }

  /* 确保区块不会被分页截断 */
  .category-section,
  .summary-table-wrapper {
    page-break-inside: avoid;
  }
}
</style>
